import { Logger } from '@sitecore-search/common';
import { Actions } from '@sitecore-search/core';
import { trackSearchResultsClickEntityEvent } from '@sitecore-search/data';
import { SearchResultsActionTypes } from '@sitecore-search/widgets';
import useBaseWidget from '../../../hooks/useBaseWidget.js';
import useQuery from '../../../hooks/useQuery.js';
import useWidget from '../../../hooks/useWidget.js';
import useWidgetActionsHandler from '../../../hooks/useWidgetActionsHandler.js';
import useWidgetActionsReducer from '../../../hooks/useWidgetActionsReducer.js';
import useWidgetRef from '../../../hooks/useWidgetRef.js';
import useWidgetState from '../../../hooks/useWidgetState.js';
import { getSearchEventData } from '../../../utils/events.js';
import handleFacetChangedAction from '../actionHandlers/facetChanged/index.js';
import handleRemoveFilterAction from '../actionHandlers/removeFilter/index.js';
import initializeQueries from '../queryInitalizers/index.js';
import facetClickedReducer from '../reducers/facetClicked/index.js';
import removeFilterReducer from '../reducers/removeFilter/index.js';
import widgetDataRequestSuccessReducer from '../reducers/widgetDataRequestSuccess/index.js';
const contextReducer = (state, action) => {
    switch (action.type) {
        case Actions.WIDGET_DATA_REQUEST_SUCCESS: {
            return widgetDataRequestSuccessReducer(state);
        }
        // @deprecated action: SearchResultsActionTypes.FILTER_CLICKED
        case SearchResultsActionTypes.FILTER_CLICKED:
        case SearchResultsActionTypes.REMOVE_FILTER: {
            return removeFilterReducer(state, action.payload);
        }
        case SearchResultsActionTypes.FACET_CLICKED: {
            return facetClickedReducer(state, action.payload);
        }
        case SearchResultsActionTypes.SORT_CHANGED:
            return {
                ...state,
                page: 1,
                sortType: action.payload.name,
            };
        case SearchResultsActionTypes.PAGE_NUMBER_CHANGED:
            return {
                ...state,
                page: action.payload.page,
            };
        case SearchResultsActionTypes.RESULTS_PER_PAGE_CHANGED:
            return {
                ...state,
                page: 1,
                itemsPerPage: action.payload.numItems,
            };
        case SearchResultsActionTypes.KEYPHRASE_CHANGED:
            return {
                ...state,
                page: 1,
                keyphrase: action.payload.keyphrase,
                selectedFacets: [], // change in keyphrase resets facets
            };
        case SearchResultsActionTypes.CLEAR_FILTERS:
            return {
                ...state,
                page: 1,
                selectedFacets: [],
            };
        default:
            return state;
    }
};
function useSearchResults(config) {
    const { rfkId, dispatch } = useBaseWidget();
    const control = useWidget();
    if (!control.isInitialized()) {
        control.initialize(config || {});
        initializeQueries(control);
    }
    const { query, options, actions } = control;
    const state = useWidgetState();
    const queryResult = useQuery(query);
    const widgetRef = useWidgetRef();
    useWidgetActionsReducer(contextReducer, []);
    useWidgetActionsHandler((action) => {
        switch (action.type) {
            case SearchResultsActionTypes.FACET_CLICKED: {
                handleFacetChangedAction(query, action.payload);
                break;
            }
            // @deprecated action: SearchResultsActionTypes.FILTER_CLICKED
            case SearchResultsActionTypes.FILTER_CLICKED:
            case SearchResultsActionTypes.REMOVE_FILTER: {
                handleRemoveFilterAction(query, action.payload);
                break;
            }
            case SearchResultsActionTypes.ITEM_CLICKED: {
                const { index, id, entity, sourceId } = action.payload;
                trackSearchResultsClickEntityEvent(rfkId, entity, {
                    index,
                    items: [{ id, sourceId }],
                    ...getSearchEventData(query),
                });
                break;
            }
            case SearchResultsActionTypes.SORT_CHANGED: {
                query
                    .getRequest()
                    .resetSearchSortCriteria()
                    .setSearchSortCriteriaName(action.payload.name)
                    .setSearchOffset(0);
                break;
            }
            case SearchResultsActionTypes.PAGE_NUMBER_CHANGED: {
                const limit = query.getRequest().getSearchLimit() ?? query.getLastResponse()?.limit;
                if (typeof limit === 'undefined') {
                    Logger.warn(`Widget "${rfkId}" has triggered a onPageNumberChanged action and there is no itemsPerPage state or query search limit defined`);
                }
                else {
                    query.getRequest().setSearchOffset(limit * (action.payload.page - 1));
                }
                break;
            }
            case SearchResultsActionTypes.RESULTS_PER_PAGE_CHANGED: {
                query.getRequest().setSearchLimit(action.payload.numItems).setSearchOffset(0);
                break;
            }
            case SearchResultsActionTypes.KEYPHRASE_CHANGED: {
                query
                    .getRequest()
                    .resetSearchFacetTypeFilters() // change in keyphrase resets facets
                    .setSearchOffset(0); // change in keyphrase resets page
                if (!action.payload.keyphrase) {
                    query.getRequest().resetSearchQueryKeyphrase();
                }
                else {
                    query.getRequest().setSearchQueryKeyphrase(action.payload.keyphrase);
                }
                break;
            }
            case SearchResultsActionTypes.CLEAR_FILTERS: {
                query.getRequest().resetSearchFacetTypeFilters().setSearchOffset(0);
                break;
            }
        }
    }, [rfkId, query]);
    return { widgetRef, rfkId, state, queryResult, actions, options, dispatch, query };
}
export default useSearchResults;
